/**
 * V2 Modal — a centered overlay dialog on a dimmed backdrop, themed to V2
 * tokens. Closes on backdrop click, the X button, and Escape. The panel is a
 * V2 Card surface. Minimal by design: callers supply title/subtitle/footer.
 */
import { useEffect } from 'react';
import type { ReactNode } from 'react';

import { Card } from './card';
import { IconX } from '../icons';
import { cn } from '../../../lib/utils';

/** Props for the V2 Modal. */
export interface ModalProps {
  /** Accessible dialog label (aria-label). */
  label: string;
  title?: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** Right-aligned footer actions. */
  footer?: ReactNode;
  /** Width override (default max-w-xl). */
  className?: string;
  /** data-testid passthrough on the dialog root. */
  testId?: string;
  /** data-state passthrough (e.g. the dialog's classification state). */
  state?: string;
}

/** V2 overlay modal. */
export function Modal({
  label,
  title,
  subtitle,
  onClose,
  children,
  footer,
  className,
  testId,
  state,
}: ModalProps) {
  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label}
      data-testid={testId}
      data-state={state}
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/55 p-4 pt-20"
      onClick={onClose}
    >
      <Card
        // Cap the panel to the viewport and lay it out as a column so a long
        // body scrolls INSIDE the card. Without this, a modal with a lot of
        // content (B-932's review list on a 200-item estimate) grew past the
        // bottom of a short laptop window and pushed its own footer buttons —
        // including Close — out of reach.
        className={cn(
          'w-full max-w-xl p-0 overflow-hidden flex flex-col max-h-[calc(100vh-6rem)]',
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || subtitle) && (
          <div className="shrink-0 flex items-start justify-between gap-2 border-b border-[var(--bd-subtle)] px-4 py-3">
            <div className="min-w-0">
              {title && <h4 className="text-[15px] font-[580] text-[var(--tx-hi)]">{title}</h4>}
              {subtitle && <p className="mt-1 text-[11.5px] text-[var(--tx-lo)]">{subtitle}</p>}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-[var(--r-md)] p-1 text-[var(--tx-lo)] hover:bg-[var(--bg-3)] hover:text-[var(--tx-hi)] [&_svg]:w-4 [&_svg]:h-4"
            >
              <IconX />
            </button>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{children}</div>
        {footer && (
          <div className="shrink-0 flex flex-wrap justify-end gap-2 border-t border-[var(--bd-subtle)] px-4 py-3">
            {footer}
          </div>
        )}
      </Card>
    </div>
  );
}
